/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

export type ColdRoutableProject = {
  status?: string;
  migration_status?: string;
} | null;

/**
 * Project detail lives on COLD after archive + successful migrate.
 * Nginx routes `{origin}/archived/api_user/...` to the archived server.
 * Archived projects that are not yet MIGRATED still use the primary
 * `/api_user` host; the backend allows GET only on those too.
 */
export function isProjectOnColdStorage(project: ColdRoutableProject): boolean {
  return project?.status === 'ARCHIVED' && project?.migration_status === 'MIGRATED';
}

/**
 * Catalog / auth / platform-admin paths stay on the primary (HOT) server
 * even when a COLD project is selected, so the project list and session
 * still resolve.
 */
const HOT_API_USER_PATHS = [
  /^\/server_user(?:\/|$)/,
  /^\/projects(?:\/|$)/,
  /^\/project_groups(?:\/|$)/,
  /^\/server_users(?:\/|$)/,
  /^\/boxes(?:\/|$)/,
  /^\/org_configs(?:\/|$)/,
  /^\/orgs(?:\/|$)/,
];

export function shouldUseArchivedApiPrefix(project: ColdRoutableProject, path: string): boolean {
  if (!isProjectOnColdStorage(project)) {
    return false;
  }
  const pathname = path.split('?')[0];
  return !HOT_API_USER_PATHS.some((pattern) => pattern.test(pathname));
}

export function apiUserBaseUrl(serverUrl: string, useArchived: boolean): string {
  const prefix = useArchived ? '/archived' : '';
  return `${serverUrl}${prefix}/api_user`;
}
